import React, { useState, useRef } from "react";
import { X, Mic, Image, MessageSquare, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface EchoLoveNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  boothId: string;
  boothName: string;
  cityName?: string;
  countryName?: string;
  eventId?: string; // Optional event ID for linking to exhibition events
  onSave?: () => void;
}

const EchoLoveNoteModal: React.FC<EchoLoveNoteModalProps> = ({
  isOpen,
  onClose,
  boothId,
  boothName,
  cityName,
  countryName,
  eventId,
  onSave,
}) => {
  const { user } = useAuthContext();
  const [memoryType, setMemoryType] = useState<"voice" | "photo" | "phrase" | "mood">("phrase");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [phrase, setPhrase] = useState("");
  const [mood, setMood] = useState("");
  const [culturalExchange, setCulturalExchange] = useState("");
  const [saving, setSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 10) {
            stopRecording();
            return 10;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Handle photo selection
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save love note
  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      let voiceNoteUrl: string | undefined;
      let photoUrl: string | undefined;

      // Upload voice note if present
      if (audioBlob && memoryType === "voice") {
        const fileName = `love-notes/${user.id}/${Date.now()}.webm`;
        const { data, error } = await supabase.storage
          .from("profile-videos")
          .upload(fileName, audioBlob, { contentType: "audio/webm" });

        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage
          .from("profile-videos")
          .getPublicUrl(fileName);
        voiceNoteUrl = publicUrl;
      }

      // Upload photo if present
      if (photoFile && memoryType === "photo") {
        const fileName = `love-notes/${user.id}/${Date.now()}.jpg`;
        const { data, error } = await supabase.storage
          .from("profile-photos")
          .upload(fileName, photoFile, { contentType: "image/jpeg" });

        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage
          .from("profile-photos")
          .getPublicUrl(fileName);
        photoUrl = publicUrl;
      }

      // Save to database (exhibition_love_notes table)
      const { data: savedNote, error: saveError } = await (supabase as any)
        .from("exhibition_love_notes")
        .insert({
          user_id: user.id,
          booth_id: boothId,
          event_id: eventId || null,
          memory_type: memoryType,
          voice_note_url: voiceNoteUrl || null,
          photo_url: photoUrl || null,
          shared_phrase: memoryType === "phrase" ? phrase : null,
          mood: memoryType === "mood" ? mood : null,
          cultural_exchange: culturalExchange || null,
          booth_name: boothName,
          city_name: cityName || null,
          country_name: countryName || null,
        })
        .select()
        .single();

      if (saveError) {
        console.error("Error saving love note to database:", saveError);
        throw saveError;
      }

      toast.success("Love note saved! 💖");
      onSave?.();
      onClose();

      // Reset form
      setMemoryType("phrase");
      setPhrase("");
      setMood("");
      setCulturalExchange("");
      setAudioBlob(null);
      setPhotoFile(null);
      setPhotoPreview(null);
    } catch (error) {
      console.error("Error saving love note:", error);
      toast.error("Failed to save love note");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" />
            Capture This Moment
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Memory Type Selection */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {(["voice", "photo", "phrase", "mood"] as const).map((type) => (
            <button
              key={type}
              onClick={() => setMemoryType(type)}
              className={cn(
                "p-3 rounded-lg border-2 transition-all",
                memoryType === type
                  ? "border-rose-500 bg-rose-50"
                  : "border-gray-200 hover:border-rose-200"
              )}
            >
              {type === "voice" && <Mic className="w-5 h-5 mx-auto text-rose-500" />}
              {type === "photo" && <Image className="w-5 h-5 mx-auto text-rose-500" />}
              {type === "phrase" && <MessageSquare className="w-5 h-5 mx-auto text-rose-500" />}
              {type === "mood" && <Heart className="w-5 h-5 mx-auto text-rose-500" />}
              <div className="text-xs mt-1 capitalize">{type}</div>
            </button>
          ))}
        </div>

        {/* Voice Recording */}
        {memoryType === "voice" && (
          <div className="space-y-4 mb-4">
            {!recording && !audioBlob && (
              <Button onClick={startRecording} className="w-full" variant="outline">
                <Mic className="w-4 h-4 mr-2" />
                Start Recording (10s max)
              </Button>
            )}
            {recording && (
              <div className="text-center space-y-2">
                <div className="text-2xl font-bold text-rose-500">{recordingTime}s</div>
                <Button onClick={stopRecording} variant="destructive" className="w-full">
                  Stop Recording
                </Button>
              </div>
            )}
            {audioBlob && (
              <div className="text-center text-sm text-muted-foreground">
                Voice note recorded! Ready to save.
              </div>
            )}
          </div>
        )}

        {/* Photo Upload */}
        {memoryType === "photo" && (
          <div className="space-y-4 mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />
            {!photoPreview && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                variant="outline"
              >
                <Image className="w-4 h-4 mr-2" />
                Choose Photo
              </Button>
            )}
            {photoPreview && (
              <div className="space-y-2">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <Button
                  onClick={() => {
                    setPhotoFile(null);
                    setPhotoPreview(null);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Change Photo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Phrase Input */}
        {memoryType === "phrase" && (
          <Textarea
            placeholder="Share a phrase or moment that touched your heart..."
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            className="mb-4 min-h-[100px]"
          />
        )}

        {/* Mood Input */}
        {memoryType === "mood" && (
          <Input
            placeholder="How are you feeling? (e.g., 'Butterflies', 'Warm & Cozy')"
            value={mood}
            onChange={(e) => setMood(e.target.value)}
            className="mb-4"
          />
        )}

        {/* Cultural Exchange (optional) */}
        <Textarea
          placeholder="Any cultural exchange or moment to remember? (optional)"
          value={culturalExchange}
          onChange={(e) => setCulturalExchange(e.target.value)}
          className="mb-4 min-h-[60px]"
        />

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || (memoryType === "phrase" && !phrase) || (memoryType === "mood" && !mood) || (memoryType === "voice" && !audioBlob) || (memoryType === "photo" && !photoFile)}
          className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Heart className="w-4 h-4 mr-2" />
              Save Love Note 💖
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EchoLoveNoteModal;

